import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

type ToastType = "success" | "error" | "info";

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  action?: { label: string; onClick: () => void };
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, action?: { label: string; onClick: () => void }) => void;
}

const ToastContext = createContext<ToastContextType>({ showToast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

const iconMap: Record<ToastType, string> = {
  success: "ri-check-line",
  error: "ri-error-warning-line",
  info: "ri-information-line",
};

const colorMap: Record<ToastType, string> = {
  success: "border-green-300 bg-green-50 text-green-800",
  error: "border-red-300 bg-red-50 text-red-800",
  info: "border-blue-300 bg-blue-50 text-blue-800",
};

const iconColorMap: Record<ToastType, string> = {
  success: "text-green-600",
  error: "text-red-600",
  info: "text-blue-600",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  let nextId = 0;

  const showToast = useCallback((message: string, type: ToastType = "info", action?: { label: string; onClick: () => void }) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type, action }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed top-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto flex flex-col gap-1.5 px-4 py-3 rounded-lg border shadow-sm text-sm font-medium animate-slide-in ${colorMap[toast.type]}`}
          >
            <div className="flex items-center gap-2.5">
              <i className={`${iconMap[toast.type]} ${iconColorMap[toast.type]} text-base`} />
              {toast.message}
            </div>
            {toast.action && (
              <button
                onClick={(e) => { e.stopPropagation(); toast.action!.onClick(); }}
                className="text-xs underline text-left hover:opacity-80 transition-opacity ml-7"
              >
                {toast.action.label} →
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}